import { GetAppointment } from "@/services/fhir";
import { isTeleconsultoriaAppointment } from "../utils/teleconsultoriaAppointment";

type AppointmentStatus = "booked" | "pending" | "proposed" | "arrived" | "fulfilled";

export interface TeleconsultoriaAppointmentsQuery {
  /** CSV de `Practitioner/...` (obrigatório: o scheduling exige actor, como no dashboard). */
  practitionerReferences: string;
  startDate?: string;
  endDate?: string;
  statuses: AppointmentStatus[];
  includeLocation?: boolean;
}

/**
 * Teleconsultorias (appointmentType TELECONSULTORIA) dos profissionais.
 * Mesma forma de busca do dashboard (`actor=Practitioner...`, `includePatient`):
 * sem o actor o scheduling responde 409. Devolve as entries (Appointment +
 * Patient/Location incluídos) só dos Appointments de teleconsultoria.
 */
export async function searchTeleconsultoriaAppointmentEntries(
  query: TeleconsultoriaAppointmentsQuery,
): Promise<fhir5.BundleEntry[]> {
  if (!query.practitionerReferences) return [];
  const bundle: fhir5.Bundle | undefined = await GetAppointment({
    professionalActor: query.practitionerReferences,
    status: query.statuses,
    includePatient: true,
    includeLocation: query.includeLocation,
    scheduling: true,
    sort: "date",
    ...(query.startDate && { startDate: query.startDate }),
    ...(query.endDate && { endDate: query.endDate }),
  });
  const entries = bundle?.entry || [];
  const appointments = entries.filter(
    (entry) =>
      entry.resource?.resourceType === "Appointment" &&
      isTeleconsultoriaAppointment(entry.resource as fhir5.Appointment),
  );
  const includedResources = entries.filter(
    (entry) => entry.resource?.resourceType !== "Appointment",
  );
  return [...appointments, ...includedResources];
}

function entryKey(entry: fhir5.BundleEntry): string {
  return `${entry.resource?.resourceType}/${entry.resource?.id}`;
}

/**
 * Soma ao bundle do dashboard as teleconsultorias marcadas na PRESTADORA
 * (Location dela) em que o profissional da AFILIADA é o solicitante.
 * Sem duplicar (chave resourceType/id). Falha da busca extra não quebra o
 * dashboard: devolve o bundle original.
 */
export async function appendTeleconsultoriaAppointments<T extends fhir5.Bundle | undefined>(
  bundle: T,
  query: TeleconsultoriaAppointmentsQuery,
): Promise<T> {
  try {
    const extraEntries = await searchTeleconsultoriaAppointmentEntries(query);
    if (!extraEntries.length) return bundle;
    const currentEntries = bundle?.entry || [];
    const seen = new Set(currentEntries.map(entryKey));
    const newEntries = extraEntries.filter((entry) => !seen.has(entryKey(entry)));
    if (!newEntries.length) return bundle;
    return {
      ...(bundle || { resourceType: "Bundle", type: "searchset" }),
      entry: [...currentEntries, ...newEntries],
    } as T;
  } catch (error) {
    console.error("appendTeleconsultoriaAppointments", error);
    return bundle;
  }
}
